import type { DowntimeCause, DowntimeType, SlaTier } from '@/api/services';

/** @ui tone union (subset used here). */
type Tone = 'accent' | 'danger' | 'info' | 'neutral' | 'primary' | 'success' | 'warning';

/* ── SLA tier ──────────────────────────────────────────────────────────── */

export const SLA_TIER_TONE: Record<SlaTier, Tone> = {
  bronze: 'warning',
  silver: 'neutral',
  gold: 'accent',
  platinum: 'info',
  custom: 'primary',
};

/** Reference metrics per tier (legacy `SLA_TIER_CONFIG`) — for template hints. */
export const SLA_TIER_UPTIME: Record<Exclude<SlaTier, 'custom'>, number> = {
  bronze: 99.0,
  silver: 99.5,
  gold: 99.9,
  platinum: 99.99,
};

/* ── Downtime type / cause ─────────────────────────────────────────────── */

export const DOWNTIME_TYPE_LABELS: Record<DowntimeType, string> = {
  unplanned: 'Unplanned',
  planned: 'Planned',
  maintenance: 'Maintenance',
  emergency: 'Emergency',
};

export const DOWNTIME_TYPE_TONE: Record<DowntimeType, Tone> = {
  unplanned: 'danger',
  planned: 'info',
  maintenance: 'warning',
  emergency: 'danger',
};

export const DOWNTIME_CAUSE_LABELS: Record<DowntimeCause, string> = {
  provider: 'Provider (our side)',
  customer: 'Customer',
  third_party: 'Third party',
  force_majeure: 'Force majeure',
  scheduled: 'Scheduled maintenance',
};

export const DOWNTIME_CAUSE_TONE: Record<DowntimeCause, Tone> = {
  provider: 'danger',
  customer: 'neutral',
  third_party: 'accent',
  force_majeure: 'warning',
  scheduled: 'success',
};

/** Whether a cause counts against SLA by default (drives the form toggle). */
export const DOWNTIME_CAUSE_COUNTS_SLA: Record<DowntimeCause, boolean> = {
  provider: true,
  customer: false,
  third_party: true,
  force_majeure: false,
  scheduled: false,
};

export const DOWNTIME_TYPE_OPTIONS = (Object.keys(DOWNTIME_TYPE_LABELS) as DowntimeType[]).map(
  (v) => ({ value: v, label: DOWNTIME_TYPE_LABELS[v] }),
);

export const DOWNTIME_CAUSE_OPTIONS = (Object.keys(DOWNTIME_CAUSE_LABELS) as DowntimeCause[]).map(
  (v) => ({ value: v, label: DOWNTIME_CAUSE_LABELS[v] }),
);

/* ── Helpers ───────────────────────────────────────────────────────────── */

/** Normalise the `template_tier` field (backend returns it wrapped or bare). */
export function resolveTier(tier: unknown): SlaTier | null {
  if (!tier) return null;
  if (typeof tier === 'string') return tier as SlaTier;
  if (typeof tier === 'object' && 'sla_tier' in tier) {
    const t = (tier as { sla_tier: string }).sla_tier;
    return t ? (t as SlaTier) : null;
  }
  return null;
}

/** Minutes → compact human duration (e.g. 216 → "3h 36m"). */
export function formatMinutes(minutes?: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
